import { BasicBetterBest } from '@/services/catalogue/useBasicBetterBest';
import { Value } from '@udecode/plate-common';
import { z } from 'zod';
import { Company } from '../companies';
import { Partner } from '../partners';
import { Region } from '../region';

export const Category = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  isMainDevice: z.boolean(),
  hiddenFromTenantIds: z.array(z.string().uuid()).optional()
});

export type Category = z.infer<typeof Category>;
export const Manufacturer = z.object({
  id: z.string(),
  name: z.string()
});

export type Manufacturer = z.infer<typeof Manufacturer>;
export const ImageDetails = z.object({
  id: z.string(),
  original: z.string(),
  thumbnail: z.string(),
  hash: z.string(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
  ratio: z.number()
});

export type ImageDetails = z.infer<typeof ImageDetails>;

export const PropertyBagSpec = z.object({
  iconId: z.string().optional(),
  text: z.string()
});
export type PropertyBagSpec = z.infer<typeof PropertyBagSpec>;

export const PropertyBag = z.array(PropertyBagSpec);

export type PropertyBag = z.infer<typeof PropertyBag>;
export enum MarginType {
  None = 0,
  Item = 1,
  Manufacturer = 2,
  Category = 4,
  Composite = 6,
  Global = 8,
  Base = 16
}

export const PriceBreakdownMargin = z.object({
  party: z.string(),
  margin: z.number(),
  marginType: z.nativeEnum(MarginType),
  contribution: z.number()
});

export type PriceBreakdownMargin = z.infer<typeof PriceBreakdownMargin>;

export const PriceBreakdown = z.object({
  date: z.coerce.date(),
  price: z.number(),
  margins: z.array(PriceBreakdownMargin)
});

export type PriceBreakdown = z.infer<typeof PriceBreakdown>;
export const Product = z.object({
  id: z.string(),
  productId: z.string(),
  name: z.string(),
  code: z.string(),
  description: z
    .object({
      value: z.custom<Value>()
    })
    .optional(),
  propertyBag: z.object({
    properties: PropertyBag,
    basicBetterBest: z.nativeEnum(BasicBetterBest).optional(),
    triggerTypes: z.array(z.string()).optional(),
    sharedLimitGroup: z.string().optional(),
    bondedItemIds: z.array(z.string()).optional(),
    groupMaxQuantity: z.number().optional(),
    requiresSerialNumber: z.boolean().optional(),
    basePackForRequestType: z.string().optional()
  }),
  imageUri: ImageDetails.optional(),
  manufacturer: Manufacturer,
  category: Category,
  price: PriceBreakdown.optional(),
  region: Region,
  isRateCardItem: z.boolean().optional()
});

export type Product = z.infer<typeof Product>;

export const ProductDiffs = z.object({
  added: z.array(Product),
  removed: z.array(Product),
  changed: z.array(
    z.object({
      old: Product,
      new: Product
    })
  )
});
export type ProductDiffs = z.infer<typeof ProductDiffs>;

export const EffectiveDate = z.object({
  value: z.coerce.date(),
  invalidatedOn: z.coerce.date().optional()
});

export type EffectiveDate = z.infer<typeof EffectiveDate>;

export const BaseMargin = z.object({
  modifier: z.number(),
  effectiveDate: EffectiveDate
});

export type BaseMargin = z.infer<typeof BaseMargin>;

export const BaseMarginItem = z.object({
  daasCatalogItem: z.object({ id: z.string() }),
  modifier: z.number(),
  effectiveDate: EffectiveDate
});

export type BaseMarginItem = z.infer<typeof BaseMarginItem>;

export const Price = z.object({
  price: z.number(),
  effectiveDate: EffectiveDate
});

export type Price = z.infer<typeof Price>;

export const ReplacedProduct = z.object({
  id: z.string(),
  replacedProduct: Product,
  replacementProduct: Product,
  effectiveDate: EffectiveDate
});

export type ReplacedProduct = z.infer<typeof ReplacedProduct>;
export const Filter = z.object({
  id: z.string(),
  daasCatalogItem: Product.optional(),
  manufacturer: Manufacturer.optional(),
  category: Category.optional(),
  effectiveDate: EffectiveDate,
  modifier: z.number()
});

export type Filter = z.infer<typeof Filter>;

export const FilterPack = z.object({
  id: z.string(),
  globalMarginModifier: z.number(),
  effectiveDate: EffectiveDate,
  marginFilters: z.array(Filter),
  productFilters: z.array(Filter)
});

export type FilterPack = z.infer<typeof FilterPack>;

export const MarginFilter = z.object({
  id: z.string(),
  daasCatalogItem: Product.optional(),
  manufacturer: Manufacturer.optional(),
  partner: Partner.optional(),
  company: Company.optional(),
  category: Category.optional(),
  effectiveDate: EffectiveDate,
  modifier: z.number()
});

export const ProductFilter = z.object({
  id: z.string(),
  daasCatalogItem: Product.optional(),
  manufacturer: Manufacturer.optional(),
  partner: Partner.optional(),
  company: Company.optional(),
  category: Category.optional(),
  effectiveDate: EffectiveDate
});

export const MspCatalog = z.object({
  id: z.string(),
  partner: z.object({
    id: z.string(),
    name: z.string()
  }),
  filterPack: FilterPack
});
